// client/src/components/secure/TransactionComplianceChecklist.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DocumentManager from './DocumentManager';
import { ProtectedLink } from '../../utils/openProtected';

const TransactionComplianceChecklist = ({ transactionId }) => {
    const [checklist, setChecklist] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showUploaderFor, setShowUploaderFor] = useState(null); // Tracks which item shows the uploader
    const apiClient = useApiClient();

    const fetchChecklist = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await apiClient.get(`/api/compliance/status/${transactionId}`);
            setChecklist(data.checklist || []);
        } catch (err) {
            setError(err.message || 'Could not load compliance checklist.');
        } finally {
            setIsLoading(false);
        }
    }, [apiClient, transactionId]);

    useEffect(() => {
        fetchChecklist();
    }, [fetchChecklist]);

    const handleDocumentUploaded = () => {
        setShowUploaderFor(null); // Hide the uploader
        fetchChecklist();      // Refresh the checklist to show the new status
    };

    if (isLoading) return <p className="text-slate-500">Loading checklist...</p>;
    if (error) return <p className="text-red-500">{error}</p>;

    return (
        <div>
            <h3 className="text-lg font-bold text-slate-700 mb-4">Compliance Checklist</h3>
            <ul className="space-y-3">
                {checklist.map(item => (
                    <li key={item.name} className="p-3 bg-white border rounded-md">
                        <div className="flex justify-between items-center">
                            <span className="font-medium text-slate-800">{item.name}</span>
                            {item.status === 'Submitted' ? (
                                <ProtectedLink href={`/${item.file?.file_path}`} className="px-3 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full hover:bg-green-300">
                                    ✓ Submitted
                                </ProtectedLink>
                            ) : (
                                <span className="px-3 py-1 text-xs font-semibold text-red-800 bg-red-200 rounded-full">
                                    ✗ Missing
                                </span>
                            )}
                        </div>
                        {item.status === 'Missing' && (
                            <div className="mt-3">
                                {showUploaderFor === item.name ? (
                                    <DocumentManager
                                        category="Transaction Document"
                                        transactionId={transactionId}
                                        documentName={item.name} // --- THE CRITICAL FIX ---
                                        onUploadComplete={handleDocumentUploaded}
                                    />
                                ) : (
                                    <button
                                        onClick={() => setShowUploaderFor(item.name)}
                                        className="text-sm text-indigo-600 hover:underline"
                                    >
                                        Upload Document
                                    </button>
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default TransactionComplianceChecklist;